import React, { useState } from "react"
import { Modal, Upload, Button, message } from "antd"
import {
  UploadOutlined,
  CloseOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from "@ant-design/icons"
import { translations } from "../../../utils/Translation/staticTranslation"
import {
  validateExcelFile,
  getCampaignPublicUrl,
} from "../../../utils/campaignsS3Upload"
import { Campaigns } from "../../../Apis/campaignsApi"
import "../Campaigns.css"

const { Dragger } = Upload

const UploadExcelModal = ({
  visible,
  onClose,
  onUpload,
  existingFile = null,
  viewMode = false,
  campaignId = null,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage
  const [uploading, setUploading] = useState(false)
  const [fileList, setFileList] = useState([])
  const [deleting, setDeleting] = useState(false)

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(
        t?.audience?.please_select_excel_file ||
          "Please select an Excel file to upload",
      )
      return
    }

    const file = fileList[0].originFileObj || fileList[0]
    if (!file) {
      message.error(
        t?.audience?.invalid_file || "Invalid file. Please try again.",
      )
      return
    }

    try {
      setUploading(true)
      validateExcelFile(file, 10) // 10MB max

      // Delete existing file if present
      if (existingFile) {
        try {
          await Campaigns.deleteExcel(existingFile)
        } catch (deleteError) {
          console.error("Error deleting old file:", deleteError)
        }
      }

      // Upload via backend API
      const response = await Campaigns.uploadCampaignExcel(file)

      const fileKey = response.file_key

      message.success(
        response.message || 
        t?.audience?.excel_uploaded_successfully ||
        "Excel file uploaded successfully",
      )

      // Call onUpload with the file key
      if (onUpload) {
        onUpload(fileKey)
      }

      // Reset and close
      setFileList([])
      onClose()
    } catch (error) {
      console.error("Upload error:", error)
      
      // Default error message
      let errorMessage = t?.audience?.failed_to_upload_excel || "Failed to upload Excel file"
      
      // Handle API error responses
      if (error.response?.data?.error) {
        const errorData = error.response.data.error
        
        try {
          if (typeof errorData === 'string') {
            // Use regex to extract the error message directly
            // Match: 'error': 'message' or "error": "message"
            const pattern = lang === 'ar' 
              ? /'errorArabic':\s*"([^"]+)"|'errorArabic':\s*'([^']+)'(?=,|})/ 
              : /'error':\s*"([^"]+)"|'error':\s*'([^']+)'(?=,|})/
            
            const match = errorData.match(pattern)
            if (match) {
              errorMessage = match[1] || match[2]
            } else {
              // Fallback: try the opposite language
              const fallbackPattern = lang === 'ar'
                ? /'error':\s*"([^"]+)"|'error':\s*'([^']+)'(?=,|})/
                : /'errorArabic':\s*"([^"]+)"|'errorArabic':\s*'([^']+)'(?=,|})/
              const fallbackMatch = errorData.match(fallbackPattern)
              if (fallbackMatch) {
                errorMessage = fallbackMatch[1] || fallbackMatch[2]
              } else {
                errorMessage = errorData
              }
            }
          } else if (typeof errorData === 'object') {
            // Handle direct object format
            errorMessage = lang === 'ar'
              ? (errorData.errorArabic || errorData.error)
              : (errorData.error || errorData.errorArabic)
          }
        } catch (parseError) {
          console.error("Error parsing error message:", parseError)
          errorMessage = errorData
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      message.error(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = () => {
    if (!existingFile) return

    try {
      // existingFile is now the S3 key (string path)
      const fileUrl = getCampaignPublicUrl(existingFile)

      const link = document.createElement("a")
      link.href = fileUrl
      link.download = existingFile.split("/").pop() || "campaign_excel.xlsx"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      message.success(t?.audience?.excel_download_started || "Download started")
    } catch (error) {
      console.error("Download error:", error)
      message.error(
        t?.audience?.failed_to_download_excel ||
          "Failed to download Excel file",
      )
    }
  }

  const handleDelete = async () => {
    if (!existingFile) return

    try {
      setDeleting(true)
      await Campaigns.deleteExcel(existingFile)

      message.success(
        t?.audience?.excel_deleted_successfully ||
          "Excel file deleted successfully",
      )

      if (onUpload) {
        onUpload(null)
      }
    } catch (error) {
      console.error("Delete error:", error)
      message.error(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          t?.audience?.failed_to_delete_excel ||
          "Failed to delete Excel file",
      )
    } finally {
      setDeleting(false)
    }
  }

  const uploadProps = {
    accept: ".xls,.xlsx,.xlsm,.csv",
    fileList,
    beforeUpload: (file) => {
      // Prevent auto upload
      setFileList([file])
      return false
    },
    onRemove: () => {
      setFileList([])
    },
    maxCount: 1,
    disabled: viewMode || uploading,
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      className="upload-excel-modal"
      wrapClassName="upload-excel-modal-wrap"
      closeIcon={<CloseOutlined className="upload-excel-modal-close-icon" />}
    >
      <div className="upload-excel-content">
        <h2 className="upload-excel-title">
          {t?.audience?.upload_excel_sheet || "Upload Excel Sheet"}
        </h2>
        <p className="upload-excel-description">
          {t?.audience?.upload_excel_description ||
            "Upload an Excel file (.xls, .xlsx, .xlsm, .csv) containing your audience list. Maximum file size: 10MB"}
        </p>

        {existingFile && (
          <div className="upload-excel-existing-file">
            <div className="upload-excel-file-info">
              <span className="upload-excel-file-name">
                {existingFile.split("/").pop() || "existing_file.xlsx"}
              </span>
              <div className="upload-excel-file-actions">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  className="upload-excel-download-btn"
                  title={t?.audience?.download || "Download"}
                  disabled={viewMode}
                />
                {!viewMode && (
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                    className="upload-excel-delete-btn"
                    title={t?.audience?.delete || "Delete"}
                    danger
                    loading={deleting}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {!viewMode && (
          <>
            <Dragger {...uploadProps} className="upload-excel-dragger">
              <p className="upload-excel-icon">
                <UploadOutlined style={{ fontSize: 48, color: "#98A2B3" }} />
              </p>
              <p className="upload-excel-text">
                {t?.audience?.click_or_drag_file ||
                  "Click or drag file to this area to upload"}
              </p>
              <p className="upload-excel-hint">
                {t?.audience?.supported_formats ||
                  "Support for .xls, .xlsx, .xlsm, .csv files"}
              </p>
            </Dragger>

            <div className="upload-excel-buttons">
              <Button onClick={onClose} className="upload-excel-cancel-btn">
                {t?.buttons?.cancel || "Cancel"}
              </Button>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                disabled={fileList.length === 0}
                className="upload-excel-upload-btn"
              >
                {t?.audience?.upload_file || "Upload File"}
              </Button>
            </div>
          </>
        )}

        {viewMode && (
          <div className="upload-excel-buttons">
            <Button onClick={onClose} className="upload-excel-cancel-btn">
              {t?.buttons?.close || "Close"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default UploadExcelModal
